import { Button, Group, Modal, NumberInput, Stack, Text } from "@mantine/core";
import { IconFolderBolt, IconInfoCircle } from "@tabler/icons-react";
import { useState, useMemo, useEffect } from "react";
import { RETURN_LABEL } from "../constants";

interface SetCreditsModalProps {
  opened: boolean;
  onClose: () => void;
  onSetCredits: (userId: string, amount: number) => void;
  teamMember: {
    allocatedCredits: number;
    businessUse: boolean;
    userId: string;
    email: string;
    role: string;
    firstName: string;
    lastName: string;
    status: string;
  } | null;
  businessBalance: number | undefined;
  isLoading?: boolean;
}

export const SetCreditsModal: React.FC<SetCreditsModalProps> = ({
  opened,
  onClose,
  onSetCredits,
  teamMember,
  businessBalance,
  isLoading = false,
}) => {
  const [targetAmount, setTargetAmount] = useState<number | "">(
    teamMember?.allocatedCredits ?? 0
  );

  // Reset target amount when modal opens or team member changes
  useEffect(() => {
    if (opened && teamMember) {
      setTargetAmount(teamMember.allocatedCredits);
    } else if (!opened) {
      // Reset when modal closes
      setTargetAmount("");
    }
  }, [opened, teamMember?.userId, teamMember?.allocatedCredits]);

  // Calculate the difference between target and current
  const difference = useMemo(() => {
    if (!teamMember || targetAmount === "") return 0;
    return (targetAmount as number) - teamMember.allocatedCredits;
  }, [targetAmount, teamMember]);

  // Determine if we're allocating or reclaiming
  const operation = useMemo(() => {
    if (difference > 0) return "allocate";
    if (difference < 0) return "reclaim";
    return "none";
  }, [difference]);

  // Check if business has enough credits to allocate
  const hasInsufficientBalance = useMemo(() => {
    if (!businessBalance || difference <= 0) return false;
    return difference > businessBalance;
  }, [difference, businessBalance]);

  const handleSetCredits = (): void => {
    if (!teamMember || targetAmount === "" || difference === 0) return;

    // Pass the difference (positive for allocation, negative for reclaim)
    onSetCredits(teamMember.userId, difference);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Set {RETURN_LABEL}s</Text>}
      size="lg"
      radius={"md"}
      padding={"lg"}
    >
      <div className="balance-header-container modal">
        <Stack>
          <div className="balance-subheader-text">
            <Text c="dimmed" fw={600}>
              BUSINESS BALANCE
            </Text>
          </div>
          <div className="balance-subtext">
            <IconFolderBolt size={24} />
            <Text size="lg" fw={600} c="#005fa3">
              {businessBalance ?? 0} {RETURN_LABEL}s
            </Text>
          </div>
        </Stack>
      </div>

      <Stack gap="md" mt="md">
        {/* Team Member's Current Credits Display */}
        <div
          className="balance-header-container modal"
          style={{ backgroundColor: "#FFF9DB", borderColor: "#FFE066" }}
        >
          <Stack>
            <div className="balance-subheader-text">
              <Text c="dimmed" fw={600}>
                TEAM MEMBER'S {RETURN_LABEL.toUpperCase()}S
              </Text>
            </div>
            <div className="balance-subtext">
              <IconFolderBolt size={24} />
              <Text size="lg" fw={600} c="#C68700">
                {teamMember?.allocatedCredits ?? 0} {RETURN_LABEL}s
              </Text>
            </div>
          </Stack>
        </div>

        {/* Target Amount Input */}
        <NumberInput
          key={teamMember?.userId} // Force re-render when team member changes
          label={`Target ${RETURN_LABEL}s`}
          description={
            operation === "allocate"
              ? `Enter the total number of ${RETURN_LABEL}s to allocate to this team member`
              : operation === "reclaim"
                ? `Enter the total number of ${RETURN_LABEL}s to reclaim from this team member`
                : `Enter the total number of ${RETURN_LABEL}s this team member should have`
          }
          placeholder="Enter target amount"
          value={targetAmount}
          onChange={(value) => setTargetAmount(value as number)}
          min={0}
          required
        />

        {/* Show only one alert: Insufficient Balance OR Change Preview */}
        {hasInsufficientBalance ? (
          <div
            className="allocate-modal-info"
            style={{
              backgroundColor: "#FFF5F5",
              padding: "12px",
              borderRadius: "8px",
              border: "1px solid #FA5252",
            }}
          >
            <IconInfoCircle size="1.2rem" color="#C92A2A" />
            <Text size="sm" c="#C92A2A">
              <strong>Insufficient balance!</strong> You need {difference}{" "}
              {RETURN_LABEL}s but only have {businessBalance} available.
            </Text>
          </div>
        ) : (
          difference !== 0 && (
            <div
              className="allocate-modal-info"
              style={{
                backgroundColor:
                  operation === "allocate" ? "#E7F5FF" : "#FFF5F5",
                padding: "12px",
                borderRadius: "8px",
                border: `1px solid ${
                  operation === "allocate" ? "#339AF0" : "#FA5252"
                }`,
              }}
            >
              <IconInfoCircle
                size="1.2rem"
                color={operation === "allocate" ? "#1971C2" : "#C92A2A"}
              />
              <Text
                size="sm"
                c={operation === "allocate" ? "#1971C2" : "#C92A2A"}
              >
                {operation === "allocate" ? (
                  <>
                    <strong>
                      Allocating {Math.abs(difference)} {RETURN_LABEL}s
                    </strong>{" "}
                    to {teamMember?.firstName} {teamMember?.lastName} (from{" "}
                    {teamMember?.allocatedCredits} → {targetAmount}). This will
                    be deducted from the business balance.
                  </>
                ) : (
                  <>
                    <strong>
                      Reclaiming {Math.abs(difference)} {RETURN_LABEL}s
                    </strong>{" "}
                    from {teamMember?.firstName} {teamMember?.lastName} (from{" "}
                    {teamMember?.allocatedCredits} → {targetAmount}). This will
                    be returned to the business balance.
                  </>
                )}
              </Text>
            </div>
          )
        )}
      </Stack>

      <Group justify="flex-end" mt="lg">
        <Button
          variant="subtle"
          onClick={onClose}
          color="gray"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSetCredits}
          disabled={
            difference === 0 ||
            hasInsufficientBalance ||
            targetAmount === "" ||
            isLoading
          }
          loading={isLoading}
          radius={"md"}
          size="md"
          color={operation === "allocate" ? "#0077CC" : "#FA5252"}
        >
          {operation === "allocate"
            ? `Allocate ${Math.abs(difference)} ${RETURN_LABEL}s`
            : operation === "reclaim"
              ? `Reclaim ${Math.abs(difference)} ${RETURN_LABEL}s`
              : `Set ${RETURN_LABEL}s`}
        </Button>
      </Group>
    </Modal>
  );
};
